'use client'

import { memo, useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useNinxl8BitThemeMode } from '@/app/(marketing)/_components/8bit'
import { NINXL_DOWNLOAD_HREF } from './nin-org-links'

type NavLinkItem = { label: string; href: string; kind: 'link' }
type NavScrollItem = { label: string; id: string; kind: 'scroll' }
type NavItem = NavLinkItem | NavScrollItem

const NAV_ITEMS: NavItem[] = [
  { label: 'Projects', id: 'projects', kind: 'scroll' },
  { label: 'Connect', id: 'connect-platform', kind: 'scroll' },
  { label: 'Contact', id: 'contact', kind: 'scroll' },
  { label: 'NINX', href: '/ninxl', kind: 'link' },
  { label: 'NINK', href: '/nink', kind: 'link' },
  { label: 'Ningenie', href: '/home', kind: 'link' },
]

/** Theme-aware NIN org logo */
const NinOrgLogo = memo(function NinOrgLogo() {
  const theme = useNinxl8BitThemeMode()
  const isDark = theme === 'dark'

  return (
    <Image
      key={theme}
      src={isDark ? '/ninx_logo_dark.svg' : '/ninx_logo_light.svg'}
      alt="NIN"
      width={32}
      height={32}
      className="h-8 w-8"
      priority
    />
  )
})

export const NinOrgNav = memo(function NinOrgNav({
  onScrollTo,
}: {
  onScrollTo?: (id: string) => void
}) {
  const pathname = usePathname() ?? ''
  const isOrgHome = pathname === '/nin'

  const scrollTo = useCallback(
    (id: string) => {
      if (onScrollTo) {
        onScrollTo(id)
        return
      }
      const target =
        document.getElementById(id) ??
        document.querySelector<HTMLElement>(`[data-section="${id}"]`)
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [onScrollTo],
  )

  return (
    <nav className="ninxl-8bit-nav sticky top-0 z-50" aria-label="NIN organization">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-8">
        <Link href="/nin" className="flex items-center gap-2.5">
          <NinOrgLogo />
          <span className="ninxl-theme-text text-[13px] font-medium tracking-[-0.15px]">
            NIN
          </span>
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {NAV_ITEMS.map((item) => {
            if (item.kind === 'link') {
              const isExternal = item.href.startsWith('http')
              const isActive =
                !isExternal &&
                (pathname === item.href || pathname.startsWith(`${item.href}/`))
              const className = `ninxl-8bit-label ninxl-theme-link ${isActive ? 'ninxl-theme-text-accent' : ''}`

              if (isExternal) {
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {item.label}
                  </a>
                )
              }

              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className={className}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              )
            }

            if (isOrgHome) {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="ninxl-8bit-label ninxl-theme-link border-0 bg-transparent cursor-pointer"
                >
                  {item.label}
                </button>
              )
            }

            return (
              <Link
                key={item.label}
                href={`/nin#${item.id}`}
                className="ninxl-8bit-label ninxl-theme-link"
              >
                {item.label}
              </Link>
            )
          })}
        </div>

        <Link
          href={NINXL_DOWNLOAD_HREF}
          className="nin-clean-cta ninxl-pixel-btn ninxl-pixel-btn-primary"
        >
          Download
        </Link>
      </div>
    </nav>
  )
})
